import React from "react";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import Table, {TableProps} from "@awsui/components-react/table";
import Header from "@awsui/components-react/header";
import PalmaresItem from "model/PalmaresItem";
import getPalmares from "api/getPalmares";
import {useQuery} from "react-query";
import useTable from "hooks/useTable";
import Pagination from "@awsui/components-react/pagination";
import TextFilter from "@awsui/components-react/text-filter";
import CollectionPreferences from "@awsui/components-react/collection-preferences";
import {useAuth} from "hooks/useAuth";
import Box from "@awsui/components-react/box";
import PositionIcon from "../../../components/icons/PositionIcon";


const buildColumnDefinitions = (
    t: TFunction,
    standings: PalmaresItem[] | undefined
): TableProps.ColumnDefinition<PalmaresItem>[] => [
    {
        id: "position",
        header: t("columns.position"),
        cell: (item: PalmaresItem) => item.position || "-",
        sortingField: "position"
    },
    {
        id: "username",
        header: t("columns.user"),
        cell: (item: PalmaresItem) =>
            <Box variant="span">
                <PositionIcon length={standings?.length} position={item.position}/>
                {item.user || "-"}
            </Box>,
        sortingField: "user"
    },
    {
        id: "points",
        header: t("columns.points"),
        cell: (item: PalmaresItem) => item.points || "-",
        sortingField: "points"
    }
];

const PalmaresTable: React.FC = () => {

    const {t} = useTranslation("palmares");
    const {t: tCommon} = useTranslation("common");
    const [{league, user}] = useAuth();

    // <LeagueRoute> wraps /palmares in App.tsx, so league is always defined here.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const leagueForApi = league!;

    const {
        isLoading: standingsIsLoading,
        data: standings,
        error: getPalmaresError
    } = useQuery<PalmaresItem[], Error>(`getPalmares-${leagueForApi.leagueId}`, () => getPalmares(leagueForApi, user?.token));

    const {
        preferences,
        setPreferences,
        items,
        collectionProps,
        filterProps,
        paginationProps,
        collectionPreferencesProps
    } = useTable(standings || [], {
        emptyStateSubtitle: getPalmaresError as Error,
        emptyStateTitle: t("empty"),
        pageSize: 10
    });

    return (
        <Table
            {...collectionProps}
            loadingText={t("loading")}
            columnDefinitions={buildColumnDefinitions(t, standings)}
            items={items}
            loading={standingsIsLoading}
            wrapLines={false}
            header={<Header>{t("historicalRanking")}</Header>}
            pagination={
                <Pagination
                    {...paginationProps}
                    ariaLabels={{
                        nextPageLabel: tCommon("table.nextPage"),
                        previousPageLabel: tCommon("table.previousPage"),
                        pageLabel: pageNumber => tCommon("table.page", {page: pageNumber})
                    }}
                />
            }
            filter={
                <TextFilter
                    {...filterProps}
                    filteringPlaceholder={tCommon("table.filterPlaceholder")}
                />
            }
            preferences={
                <CollectionPreferences
                    {...collectionPreferencesProps}
                    preferences={preferences}
                    onConfirm={({detail}) => setPreferences(detail)}
                />
            }
        />
    );
};

export default PalmaresTable;
